/**
 * Reading a subjects × conditions table from pasted text.
 *
 * The repeated-measures tests (Friedman, repeated-measures ANOVA, Cochran's Q) take one
 * row per subject and one column per condition — the layout a spreadsheet already has, so a
 * block copied from Excel or Sheets arrives tab-separated and just works.
 *
 * Separators follow `parseNumberList`'s rule: a tab or a semicolon on a line frees the
 * comma to be a decimal mark (`2,5<tab>3,5` is two numbers); otherwise commas and spaces
 * both separate. A first line that contains a word where a number should be is read as the
 * header and supplies the condition names.
 */

import { parseNumberInput } from "@/lib/parse-number";

export type MatrixProblem =
  | { kind: "ragged"; line: number; expected: number; got: number }
  | { kind: "invalid"; line: number; token: string };

export interface ParsedMatrix {
  /** One array per subject. Only complete when `problem` is null. */
  rows: number[][];
  /** Number of columns (conditions) — that of the first data row. */
  width: number;
  /** Condition names from a header line, if there was one. */
  header: string[] | null;
  problem: MatrixProblem | null;
}

const TAB_OR_SEMICOLON = /[\t;]/;
const DATA_SEPARATORS_STRICT = /[\t;\s]+/;
const DATA_SEPARATORS_LOOSE = /[\s,]+/;
const LINE_BREAK = /\r?\n/;
const WHITESPACE = /\s+/;
const NAME_SEPARATORS = /[|,\t]/;

function splitDataLine(line: string): string[] {
  const separators = TAB_OR_SEMICOLON.test(line)
    ? DATA_SEPARATORS_STRICT
    : DATA_SEPARATORS_LOOSE;
  return line.split(separators).filter((token) => token !== "");
}

function splitHeaderLine(line: string): string[] {
  let tokens: string[];
  if (line.includes("\t")) {
    tokens = line.split("\t");
  } else if (line.includes(";")) {
    tokens = line.split(";");
  } else if (line.includes(",")) {
    tokens = line.split(",");
  } else {
    tokens = line.split(WHITESPACE);
  }
  return tokens.map((token) => token.trim()).filter((token) => token !== "");
}

/** Parse pasted text into a numeric table; the first line may be a header of names. */
export function parseMatrix(raw: string): ParsedMatrix {
  const lines = raw.split(LINE_BREAK).filter((line) => line.trim() !== "");
  const rows: number[][] = [];
  let header: string[] | null = null;
  let width = 0;
  for (const [index, line] of lines.entries()) {
    const tokens = splitDataLine(line.trim());
    const values = tokens.map((token) => parseNumberInput(token));
    const bad = values.indexOf(null);
    if (bad !== -1) {
      if (index === 0) {
        header = splitHeaderLine(line.trim());
        continue;
      }
      return {
        rows,
        width,
        header,
        problem: { kind: "invalid", line: index + 1, token: tokens[bad] },
      };
    }
    if (rows.length === 0) {
      width = values.length;
    } else if (values.length !== width) {
      return {
        rows,
        width,
        header,
        problem: {
          kind: "ragged",
          line: index + 1,
          expected: width,
          got: values.length,
        },
      };
    }
    rows.push(values as number[]);
  }
  return { rows, width, header, problem: null };
}

/** The columns of a table, as arrays — the conditions. */
export function columnsOf(rows: readonly (readonly number[])[]): number[][] {
  const width = rows[0]?.length ?? 0;
  return Array.from({ length: width }, (_, j) => rows.map((row) => row[j]));
}

/**
 * Condition names for display: the typed names (separated by `|`, commas or tabs), else
 * the header line, else "Condition 1…k". A name list too short is padded with defaults.
 */
export function conditionNames(
  typed: string,
  header: readonly string[] | null,
  width: number,
  fallback: (n: number) => string
): string[] {
  const fromTyped = typed
    .split(NAME_SEPARATORS)
    .map((name) => name.trim())
    .filter((name) => name !== "");
  const source = fromTyped.length > 0 ? fromTyped : (header ?? []);
  return Array.from({ length: width }, (_, j) => source[j] ?? fallback(j + 1));
}
